import { useMemo, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { SearchSelect, type SSOption } from "../../ui/SearchSelect";
import { Drawer } from "../../Drawer";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../mobile/primitives";
import "../../../pages/Items.css";       // dt2 / iact / row-acts / badge-pill / empty-state
import "../../../pages/Maintenance.css"; // mt-empty (liste "Yükleniyor…")

/**
 * İnsan Kaynakları — İzinler sekmesi (Faz 5 UI).
 *
 * İki alt-sekme:
 *   • İzin Talepleri  — hr.leave.request.list/create/approve/reject/cancel.
 *       Çok kademeli onay: pending → (Yönetici Onayı) manager_approved → (İK Onayı) approved.
 *       Onay/red backend'de approve:leave / isManagerOf / süper-admin ile korunur; yetki yoksa
 *       FORBIDDEN döner → mutation onError toast ile yüzeye çıkar (buton canManage'e göre gösterilir).
 *   • İzin Türleri (canManage) — hr.leave.type.list/create/update/remove.
 *
 * Yazma aksiyonları yalnız canManage. Tür rengi, badge sınıfı olarak saklanır (b-info/b-success…)
 * → design-system rozeti; inline renk yok. Maaş bölümü bu sekmede yok (isSuper kullanılmaz).
 */

type LeaveStatus = "pending" | "manager_approved" | "approved" | "rejected" | "cancelled";

const REQ_STATUS_META: Record<LeaveStatus, { cls: string; label: string; icon: string }> = {
  pending: { cls: "b-warn", label: "Bekliyor", icon: "clock" },
  manager_approved: { cls: "b-info", label: "Yönetici Onayladı", icon: "check" },
  approved: { cls: "b-success", label: "Onaylandı", icon: "checkCircle" },
  rejected: { cls: "b-danger", label: "Reddedildi", icon: "x" },
  cancelled: { cls: "", label: "İptal", icon: "minus" },
};

/** İzin türü renk seçenekleri = design-system rozet sınıfları (serbest hex yerine token'lı rozet). */
const TYPE_COLORS: { value: string; label: string }[] = [
  { value: "", label: "Nötr" },
  { value: "b-primary", label: "Lacivert" },
  { value: "b-info", label: "Mavi" },
  { value: "b-success", label: "Yeşil" },
  { value: "b-warn", label: "Turuncu" },
  { value: "b-danger", label: "Kırmızı" },
];

interface TypeRow {
  id: string;
  code: string | null;
  name: string;
  paid: boolean;
  defaultDays: number;
  color: string | null;
  requiresApproval: boolean;
  isActive: boolean;
  requestCount: number;
  balanceCount: number;
}

/** Yalnız bilinen rozet sınıflarını uygula; eski/bilinmeyen renk değeri → nötr. */
function typeBadgeCls(c: string | null | undefined): string {
  return c && /^b-/.test(c) ? c : "";
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  return isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function fmtNum(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(n);
}

const MONO: React.CSSProperties = { fontFamily: "var(--font-mono)" };

export function LeaveTab({ canManage, isSuper }: { canManage: boolean; isSuper: boolean }) {
  void isSuper; // bu sekmede maaş/hassas bölüm yok — imza gereği alınır
  const isMobile = useIsMobile();
  const utils = trpc.useUtils();
  const invalidate = () => utils.hr.leave.invalidate();

  const [sub, setSub] = useState<"requests" | "types">("requests");

  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) =>
    setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  // ── Ortak veri ──
  const empQ = trpc.hr.employee.list.useQuery(undefined, { staleTime: 60_000 });
  const typesQ = trpc.hr.leave.type.list.useQuery(undefined, { staleTime: 30_000 });

  const empOptions = useMemo(
    () => (empQ.data ?? []).map((e) => ({ value: e.id, label: e.fullName, desc: e.positionTitle ?? undefined, tail: e.code ?? undefined })),
    [empQ.data],
  );
  const activeTypeOptions = useMemo(
    () => (typesQ.data ?? []).filter((t) => t.isActive).map((t) => ({ value: t.id, label: t.name, tail: `${t.defaultDays} gün` })),
    [typesQ.data],
  );

  // ── İzin talepleri (filtre + liste) ──
  const [fStatus, setFStatus] = useState<LeaveStatus | "">("");
  /** Durum filtresi seçenekleri — native <select> yerine SearchSelect (komşularla aynı 32px). */
  const fStatusOptions = useMemo<SSOption[]>(
    () => Object.entries(REQ_STATUS_META).map(([v, m]) => ({ value: v, label: m.label, icon: m.icon })),
    [],
  );
  const [fEmployee, setFEmployee] = useState<string | null>(null);
  const reqInput = useMemo(
    () => ({ status: (fStatus || undefined) as LeaveStatus | undefined, employeeId: fEmployee || undefined }),
    [fStatus, fEmployee],
  );
  const reqListQ = trpc.hr.leave.request.list.useQuery(reqInput, { staleTime: 5_000 });
  const rows = reqListQ.data ?? [];

  // ── Talep ekleme drawer'ı ──
  const [reqOpen, setReqOpen] = useState(false);
  const [rEmp, setREmp] = useState<string | null>(null);
  const [rType, setRType] = useState<string | null>(null);
  const [rStart, setRStart] = useState("");
  const [rEnd, setREnd] = useState("");
  const [rReason, setRReason] = useState("");
  const [reqErr, setReqErr] = useState<string | null>(null);
  function resetReqForm() {
    setREmp(null); setRType(null); setRStart(""); setREnd(""); setRReason(""); setReqErr(null);
  }
  const reqValid = !!rEmp && !!rType && !!rStart && !!rEnd && rEnd >= rStart;

  // ── Red / iptal onay diyalogları ──
  const [rejectTarget, setRejectTarget] = useState<{ id: string; code: string | null; who: string } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectErr, setRejectErr] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<{ id: string; code: string | null; who: string } | null>(null);

  // ── İzin türü drawer'ı + silme ──
  const [typeOpen, setTypeOpen] = useState(false);
  const [typeEdit, setTypeEdit] = useState<TypeRow | null>(null);
  const [tName, setTName] = useState("");
  const [tCode, setTCode] = useState("");
  const [tColor, setTColor] = useState("");
  const [tDays, setTDays] = useState("0");
  const [tPaid, setTPaid] = useState(true);
  const [tReqApproval, setTReqApproval] = useState(true);
  const [tActive, setTActive] = useState(true);
  const [typeErr, setTypeErr] = useState<string | null>(null);
  const [delType, setDelType] = useState<TypeRow | null>(null);
  const typeValid = tName.trim().length > 0;

  function openNewType() {
    setTypeEdit(null); setTName(""); setTCode(""); setTColor(""); setTDays("0");
    setTPaid(true); setTReqApproval(true); setTActive(true); setTypeErr(null); setTypeOpen(true);
  }
  function openEditType(t: TypeRow) {
    setTypeEdit(t); setTName(t.name); setTCode(t.code ?? ""); setTColor(typeBadgeCls(t.color));
    setTDays(String(t.defaultDays)); setTPaid(t.paid); setTReqApproval(t.requiresApproval);
    setTActive(t.isActive); setTypeErr(null); setTypeOpen(true);
  }

  // ── Mutasyonlar ──
  const createReqMut = trpc.hr.leave.request.create.useMutation({
    onSuccess: (d) => { invalidate(); setReqOpen(false); resetReqForm(); pushToast({ tone: "success", message: `İzin talebi oluşturuldu (${d.code}).` }); },
    onError: (e) => setReqErr(e.message),
  });
  const approveMut = trpc.hr.leave.request.approve.useMutation({
    onSuccess: (d) => { invalidate(); pushToast({ tone: "success", message: d.status === "approved" ? "İzin İK tarafından onaylandı." : "Yönetici onayı verildi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Onaylanamadı", message: e.message }),
  });
  const rejectMut = trpc.hr.leave.request.reject.useMutation({
    onSuccess: () => { invalidate(); setRejectTarget(null); setRejectReason(""); setRejectErr(null); pushToast({ tone: "success", message: "İzin talebi reddedildi." }); },
    onError: (e) => setRejectErr(e.message),
  });
  const cancelMut = trpc.hr.leave.request.cancel.useMutation({
    onSuccess: () => { invalidate(); setCancelTarget(null); pushToast({ tone: "success", message: "İzin talebi iptal edildi." }); },
    onError: (e) => { setCancelTarget(null); pushToast({ tone: "danger", title: "İptal edilemedi", message: e.message }); },
  });
  const createTypeMut = trpc.hr.leave.type.create.useMutation({
    onSuccess: () => { invalidate(); setTypeOpen(false); pushToast({ tone: "success", message: "İzin türü eklendi." }); },
    onError: (e) => setTypeErr(e.message),
  });
  const updateTypeMut = trpc.hr.leave.type.update.useMutation({
    onSuccess: () => { invalidate(); setTypeOpen(false); pushToast({ tone: "success", message: "İzin türü güncellendi." }); },
    onError: (e) => setTypeErr(e.message),
  });
  const removeTypeMut = trpc.hr.leave.type.remove.useMutation({
    onSuccess: () => { invalidate(); setDelType(null); pushToast({ tone: "success", message: "İzin türü silindi." }); },
    onError: (e) => { setDelType(null); pushToast({ tone: "danger", title: "Silinemedi", message: e.message }); },
  });

  function submitReq() {
    if (!reqValid) { setReqErr("Personel, tür ve tarih aralığı zorunlu (bitiş ≥ başlangıç)."); return; }
    setReqErr(null);
    createReqMut.mutate({ employeeId: rEmp!, typeId: rType!, startDate: rStart, endDate: rEnd, reason: rReason.trim() || null });
  }
  function submitType() {
    if (!typeValid) { setTypeErr("İzin türü adı zorunlu."); return; }
    setTypeErr(null);
    const days = Math.max(0, Math.round(Number(tDays) || 0));
    if (typeEdit) {
      updateTypeMut.mutate({ id: typeEdit.id, name: tName.trim(), paid: tPaid, defaultDays: days, requiresApproval: tReqApproval, isActive: tActive, color: tColor || null });
    } else {
      createTypeMut.mutate({ name: tName.trim(), paid: tPaid, defaultDays: days, requiresApproval: tReqApproval, color: tColor || null, code: tCode.trim() || null });
    }
  }

  // Aksiyon overlay'leri (drawer + onay diyalogları + toast) — hem masaüstü hem mobil return'de en sonda.
  const drawers = (
    <>
      {/* ── Talep Ekle drawer ── */}
      <Drawer
        open={reqOpen} onClose={() => setReqOpen(false)} size="md" dismissible={false}
        title="İzin Talebi Ekle" subtitle="Kod otomatik üretilir (IZN-yıl-sıra)"
        footer={
          <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
            <button type="button" className="btn-md primary" onClick={submitReq} disabled={createReqMut.isPending || !reqValid}>
              {createReqMut.isPending ? "Kaydediliyor…" : "Oluştur"}
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-md ghost" onClick={() => setReqOpen(false)}>Vazgeç</button>
          </div>
        }
      >
        {reqErr ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{reqErr}</div> : null}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="form-field">
            <span className="lbl">Personel <span className="req">*</span></span>
            <SearchSelect value={rEmp} options={empOptions} onChange={setREmp}
              placeholder="Personel seç…" searchPlaceholder="Personel ara…" emptyText="Personel yok" icon="user" />
          </div>
          <div className="form-field">
            <span className="lbl">İzin Türü <span className="req">*</span></span>
            <SearchSelect value={rType} options={activeTypeOptions} onChange={setRType}
              placeholder="Tür seç…" searchPlaceholder="Tür ara…" emptyText="Aktif izin türü yok" icon="calendar" />
            {activeTypeOptions.length === 0 && !typesQ.isLoading ? (
              <span className="help">Önce "İzin Türleri" sekmesinden bir tür ekleyin.</span>
            ) : null}
          </div>
          <div className="form-row">
            <div className="form-field">
              <label className="lbl" htmlFor="lv-start">Başlangıç <span className="req">*</span></label>
              <input id="lv-start" className="form-input" type="date" value={rStart} onChange={(e) => setRStart(e.target.value)} />
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="lv-end">Bitiş <span className="req">*</span></label>
              <input id="lv-end" className="form-input" type="date" value={rEnd} onChange={(e) => setREnd(e.target.value)} />
            </div>
          </div>
          {rStart && rEnd && rEnd < rStart ? (
            <div className="form-banner-err">Bitiş tarihi başlangıçtan önce olamaz.</div>
          ) : null}
          <div className="form-field">
            <label className="lbl" htmlFor="lv-reason">Açıklama</label>
            <textarea id="lv-reason" className="form-textarea" rows={3} value={rReason} onChange={(e) => setRReason(e.target.value)}
              placeholder="İsteğe bağlı — talep gerekçesi" />
          </div>
          <span className="help">Gün sayısı, seçilen aralıktan (hafta sonu/tatil düşülerek) sunucuda hesaplanır.</span>
        </div>
      </Drawer>

      {/* ── İzin Türü drawer (ekle/düzenle) ── */}
      <Drawer
        open={typeOpen} onClose={() => setTypeOpen(false)} size="md" dismissible={false}
        title={typeEdit ? "İzin Türünü Düzenle" : "Yeni İzin Türü"}
        subtitle={typeEdit ? (typeEdit.code ?? undefined) : "Talep ve bakiye hesaplarında kullanılır"}
        footer={
          <div style={{ display: "flex", gap: 8, width: "100%", alignItems: "center" }}>
            <button type="button" className="btn-md primary" onClick={submitType} disabled={createTypeMut.isPending || updateTypeMut.isPending || !typeValid}>
              {(createTypeMut.isPending || updateTypeMut.isPending) ? "Kaydediliyor…" : typeEdit ? "Kaydet" : "Oluştur"}
            </button>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn-md ghost" onClick={() => setTypeOpen(false)}>Vazgeç</button>
          </div>
        }
      >
        {typeErr ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{typeErr}</div> : null}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="form-field">
            <label className="lbl" htmlFor="lt-name">İzin Türü Adı <span className="req">*</span></label>
            <input id="lt-name" className="form-input" value={tName} onChange={(e) => setTName(e.target.value)} placeholder="Örn. Yıllık İzin" autoFocus />
          </div>
          <div className="form-row">
            {!typeEdit ? (
              <div className="form-field">
                <label className="lbl" htmlFor="lt-code">Kod</label>
                <input id="lt-code" className="form-input" value={tCode} onChange={(e) => setTCode(e.target.value)} placeholder="Boş bırakılabilir" />
              </div>
            ) : null}
            <div className="form-field">
              <label className="lbl" htmlFor="lt-color">Renk</label>
              <select id="lt-color" className="form-select" value={tColor} onChange={(e) => setTColor(e.target.value)}>
                {TYPE_COLORS.map((c) => <option key={c.value || "none"} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="form-field">
              <label className="lbl" htmlFor="lt-days">Yıllık Hak (gün)</label>
              <input id="lt-days" className="form-input" type="number" min={0} value={tDays} onChange={(e) => setTDays(e.target.value)} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
            <label className="form-checkbox"><input type="checkbox" checked={tPaid} onChange={(e) => setTPaid(e.target.checked)} />Ücretli izin</label>
            <label className="form-checkbox"><input type="checkbox" checked={tReqApproval} onChange={(e) => setTReqApproval(e.target.checked)} />Onay gerektirir</label>
            {typeEdit ? (
              <label className="form-checkbox"><input type="checkbox" checked={tActive} onChange={(e) => setTActive(e.target.checked)} />Aktif</label>
            ) : null}
          </div>
          <span className="help">Renk, izin türünün talep listesindeki rozet rengidir. "Onay gerektirir" kapalıysa talep, yönetici adımını atlayıp doğrudan İK onayına uygundur.</span>
        </div>
      </Drawer>

      {/* ── Red onay diyaloğu (gerekçe girişli) ── */}
      <ConfirmDialog
        open={!!rejectTarget}
        title="İzin talebini reddet"
        message={
          <>
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--neutral-600)" }}>
              {rejectTarget?.who} — <span style={MONO}>{rejectTarget?.code}</span> talebini reddediyorsunuz.
            </p>
            <div className="form-field">
              <label className="lbl" htmlFor="lv-rejreason">Red gerekçesi <span className="req">*</span></label>
              <textarea id="lv-rejreason" className="form-textarea" rows={3} value={rejectReason}
                onChange={(e) => { setRejectReason(e.target.value); if (rejectErr) setRejectErr(null); }}
                placeholder="Personele iletilecek gerekçe" />
            </div>
            {rejectErr ? <div className="form-banner-err" style={{ marginTop: 8 }}>{rejectErr}</div> : null}
          </>
        }
        tone="danger" confirmLabel="Reddet" cancelLabel="Vazgeç"
        busy={rejectMut.isPending}
        onConfirm={() => { if (!rejectReason.trim()) { setRejectErr("Red gerekçesi zorunlu."); return; } rejectMut.mutate({ id: rejectTarget!.id, reason: rejectReason.trim() }); }}
        onCancel={() => { setRejectTarget(null); setRejectReason(""); setRejectErr(null); }}
      />

      {/* ── İptal onay diyaloğu ── */}
      <ConfirmDialog
        open={!!cancelTarget}
        title="İzin talebini iptal et"
        message={cancelTarget ? (
          <>{cancelTarget.who} — <span style={MONO}>{cancelTarget.code}</span> talebi iptal edilecek. Onaylı bir talepse kullanılan gün ilgili yılın bakiyesine geri döner.</>
        ) : ""}
        tone="danger" confirmLabel="İptal Et" cancelLabel="Vazgeç"
        busy={cancelMut.isPending}
        onConfirm={() => cancelTarget && cancelMut.mutate({ id: cancelTarget.id })}
        onCancel={() => setCancelTarget(null)}
      />

      {/* ── İzin türü silme onayı ── */}
      <ConfirmDialog
        open={!!delType}
        title="İzin türünü sil"
        message={delType ? (
          <>"{delType.name}" izin türü silinecek. {delType.requestCount > 0 ? `Bu türe bağlı ${delType.requestCount} talep var — silme reddedilebilir.` : "Bu işlem geri alınamaz."}</>
        ) : ""}
        tone="danger" confirmLabel="Sil" cancelLabel="Vazgeç"
        busy={removeTypeMut.isPending}
        onConfirm={() => delType && removeTypeMut.mutate({ id: delType.id })}
        onCancel={() => setDelType(null)}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  if (isMobile) {
    const mb = (c: string) => (c === "b-warn" ? "b-warning" : c || "b-neutral");
    return (
      <>
        <div className="m-stickytop">
          {canManage ? (
            <div className="m-actions">
              {sub === "requests" ? (
                <button type="button" className="btn-md primary" onClick={() => { resetReqForm(); setReqOpen(true); }}>
                  <Icon name="plus" size={14} />Talep Ekle
                </button>
              ) : (
                <button type="button" className="btn-md primary" onClick={openNewType}>
                  <Icon name="plus" size={14} />Tür Ekle
                </button>
              )}
            </div>
          ) : null}
          <div className="m-chips">
            <button type="button" className={`m-chip${sub === "requests" ? " act" : ""}`} onClick={() => setSub("requests")}>İzin Talepleri{reqListQ.data ? ` (${rows.length})` : ""}</button>
            {canManage ? (
              <button type="button" className={`m-chip${sub === "types" ? " act" : ""}`} onClick={() => setSub("types")}>İzin Türleri{typesQ.data ? ` (${typesQ.data.length})` : ""}</button>
            ) : null}
          </div>
        </div>

        {/* ── İzin Talepleri ── */}
        {sub === "requests" ? (
          <>
            <div className="m-chips">
              {([["", "Tümü"], ...Object.entries(REQ_STATUS_META).map(([v, m]) => [v, m.label] as [string, string])] as Array<[string, string]>).map(([v, lb]) => (
                <button key={v || "all"} type="button" className={`m-chip${fStatus === v ? " act" : ""}`} onClick={() => setFStatus(v as LeaveStatus | "")}>{lb}</button>
              ))}
            </div>
            <SearchSelect value={fEmployee} options={empOptions} onChange={setFEmployee}
              placeholder="Tüm personel" searchPlaceholder="Personel ara…" emptyText="Personel yok" icon="user" />

            {reqListQ.isError ? (
              <div className="m-empty">İzin talepleri yüklenemedi: {reqListQ.error.message}</div>
            ) : reqListQ.isLoading ? (
              <div className="m-empty">Yükleniyor…</div>
            ) : rows.length === 0 ? (
              <div className="m-empty">{(fStatus || fEmployee) ? "Filtreye uygun talep yok." : "İzin talebi yok"}</div>
            ) : (
              rows.map((r) => {
                const st = r.status as LeaveStatus;
                const m = REQ_STATUS_META[st] ?? REQ_STATUS_META.pending;
                const terminal = st === "rejected" || st === "cancelled";
                return (
                  <MCard pad key={r.id}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{r.employeeName}</span>
                        <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)" }}>{r.code}</span>
                      </span>
                      <span className={`badge ${mb(m.cls)} no-dot`}><Icon name={m.icon} size={11} />{m.label}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                      <span className={`badge ${mb(typeBadgeCls(r.typeColor))} no-dot`}>{r.typeName}</span>
                      {!r.paid ? <span style={{ fontSize: 11, color: "var(--neutral-500)" }}>ücretsiz</span> : null}
                    </div>
                    <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                      <div style={{ flex: 1 }}><div className="m-ovl">Tarih Aralığı</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{fmtDate(r.startDate)} – {fmtDate(r.endDate)}</div></div>
                      <div style={{ flex: "0 0 auto" }}><div className="m-ovl">Gün</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{fmtNum(r.days)}</div></div>
                    </div>
                    {canManage && !terminal ? (
                      <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                        {st === "pending" ? (
                          <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--info)" }} aria-label="Yönetici Onayı" disabled={approveMut.isPending} onClick={() => approveMut.mutate({ id: r.id, stage: "manager" })}><Icon name="check" size={16} /></button>
                        ) : null}
                        {st === "manager_approved" ? (
                          <button type="button" className="m-iconbtn m-li-act" style={{ color: "var(--success)" }} aria-label="İK Onayı" disabled={approveMut.isPending} onClick={() => approveMut.mutate({ id: r.id, stage: "hr" })}><Icon name="checkCircle" size={16} /></button>
                        ) : null}
                        {(st === "pending" || st === "manager_approved") ? (
                          <button type="button" className="m-iconbtn m-li-act danger" aria-label="Reddet" onClick={() => { setRejectReason(""); setRejectErr(null); setRejectTarget({ id: r.id, code: r.code, who: r.employeeName }); }}><Icon name="x" size={16} /></button>
                        ) : null}
                        {(st === "pending" || st === "manager_approved" || st === "approved") ? (
                          <button type="button" className="m-iconbtn m-li-act" aria-label="İptal" onClick={() => setCancelTarget({ id: r.id, code: r.code, who: r.employeeName })}><Icon name="minus" size={16} /></button>
                        ) : null}
                      </div>
                    ) : null}
                  </MCard>
                );
              })
            )}
          </>
        ) : null}

        {/* ── İzin Türleri (canManage) ── */}
        {sub === "types" && canManage ? (
          <>
            {typesQ.isError ? (
              <div className="m-empty">İzin türleri yüklenemedi: {typesQ.error.message}</div>
            ) : typesQ.isLoading ? (
              <div className="m-empty">Yükleniyor…</div>
            ) : (typesQ.data ?? []).length === 0 ? (
              <div className="m-empty">İzin türü yok</div>
            ) : (
              (typesQ.data ?? []).map((t) => (
                <MCard pad key={t.id}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className={`badge ${mb(typeBadgeCls(t.color))} no-dot`}>{t.name}</span>
                      {t.code ? <span className="mono" style={{ display: "block", fontSize: 11, color: "var(--neutral-400)", marginTop: 4 }}>{t.code}</span> : null}
                    </span>
                    <span className={`badge ${t.isActive ? "b-success" : "b-neutral"} no-dot`}><Icon name={t.isActive ? "check" : "x"} size={11} />{t.isActive ? "Aktif" : "Pasif"}</span>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                    <div style={{ flex: 1 }}><div className="m-ovl">Ücret</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{t.paid ? "Ücretli" : "Ücretsiz"}</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">Yıllık Hak</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{t.defaultDays} gün</div></div>
                    <div style={{ flex: 1 }}><div className="m-ovl">Onay</div><div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{t.requiresApproval ? "Gerekli" : "Serbest"}</div></div>
                    <div style={{ flex: "0 0 auto" }}><div className="m-ovl">Talep</div><div className="mono" style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{t.requestCount}</div></div>
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                    <button type="button" className="m-iconbtn m-li-act" aria-label={`${t.name}: düzenle`} onClick={() => openEditType(t as TypeRow)}><Icon name="edit" size={16} /></button>
                    <button type="button" className="m-iconbtn m-li-act danger" aria-label={`${t.name}: sil`} onClick={() => setDelType(t as TypeRow)}><Icon name="trash" size={16} /></button>
                  </div>
                </MCard>
              ))
            )}
          </>
        ) : null}

        {drawers}
      </>
    );
  }

  return (
    <>
      <div className="dt2-wrap">
        <div className="dt2-tabs">
          <button type="button" className={`dt2-tab ${sub === "requests" ? "active" : ""}`} onClick={() => setSub("requests")}>
            İzin Talepleri{reqListQ.data ? <span className="ct">{rows.length}</span> : null}
          </button>
          {canManage ? (
            <button type="button" className={`dt2-tab ${sub === "types" ? "active" : ""}`} onClick={() => setSub("types")}>
              İzin Türleri{typesQ.data ? <span className="ct">{typesQ.data.length}</span> : null}
            </button>
          ) : null}
        </div>

        {/* ── ALT SEKME: İzin Talepleri ── */}
        {sub === "requests" ? (
          <>
            <div className="dt2-toolbar hr-toolbar">
              <div className="dt2-filter sm">
                <SearchSelect
                  value={fStatus || null}
                  options={fStatusOptions}
                  onChange={(v) => setFStatus((v as LeaveStatus) ?? "")}
                  placeholder="Tüm durumlar"
                  showFooter={false}
                />
              </div>
              <div className="dt2-filter lg">
                <SearchSelect value={fEmployee} options={empOptions} onChange={setFEmployee}
                  placeholder="Tüm personel" searchPlaceholder="Personel ara…" emptyText="Personel yok" icon="user" />
              </div>
              {(fStatus || fEmployee) ? (
                <button type="button" className="btn-md ghost" title="Filtreleri temizle" onClick={() => { setFStatus(""); setFEmployee(null); }}>
                  <Icon name="x" size={14} />Temizle
                </button>
              ) : null}
              <span className="grow" />
              {canManage ? (
                <button type="button" className="btn-md primary" onClick={() => { resetReqForm(); setReqOpen(true); }}>
                  <Icon name="plus" size={14} />Talep Ekle
                </button>
              ) : null}
            </div>

            <div className="dt2-scroll">
              {reqListQ.isError ? (
                <div className="form-banner-err hr-listerr">İzin talepleri yüklenemedi: {reqListQ.error.message}</div>
              ) : reqListQ.isLoading ? (
                <div className="mt-empty">Yükleniyor…</div>
              ) : rows.length === 0 ? (
                <div className="empty-state">
                  <span className="ic" aria-hidden="true"><Icon name="calendar" size={28} /></span>
                  <p className="ttl">İzin talebi yok</p>
                  <p className="sub">{(fStatus || fEmployee) ? "Filtreye uygun talep yok." : canManage ? "\"Talep Ekle\" ile ilk izin talebini oluşturun." : "Görüntülenecek izin talebi bulunmuyor."}</p>
                </div>
              ) : (
                <table className="dt2">
                  <thead>
                    <tr>
                      <th style={{ width: 150 }}>Kod</th>
                      <th>Personel</th>
                      <th>Tür</th>
                      <th style={{ width: 190 }}>Tarih Aralığı</th>
                      <th className="num" style={{ width: 70 }}>Gün</th>
                      <th style={{ width: 160 }}>Durum</th>
                      {canManage ? <th className="hr-act-col">Eylemler</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const st = r.status as LeaveStatus;
                      // Bilinmeyen durumu "Bekliyor" diye göstermek YANLIŞ bilgi olur; ham değeri uyarı tonuyla bas.
                      const m = REQ_STATUS_META[st] ?? { cls: "b-warn", label: String(st), icon: "alertTriangle" };
                      const terminal = st === "rejected" || st === "cancelled";
                      return (
                        <tr key={r.id}>
                          <td className="cell-id">{r.code}</td>
                          <td>{r.employeeName}</td>
                          <td>
                            <span className={`badge-pill ${typeBadgeCls(r.typeColor)}`}>{r.typeName}</span>
                            {!r.paid ? <span className="hr-unit">ücretsiz</span> : null}
                          </td>
                          <td className="mono hr-cell-sm">{fmtDate(r.startDate)} – {fmtDate(r.endDate)}</td>
                          <td className="cell-num">{fmtNum(r.days)}</td>
                          <td><span className={`badge-pill ${m.cls}`}><Icon name={m.icon} size={11} />{m.label}</span></td>
                          {canManage ? (
                            <td>
                              {/* İkon-only aksiyonlar: metinli butonlar hücrede alt alta kırılıp
                                  satırı üç kat yükseltiyordu. Aynı anda en fazla 3 buton görünür. */}
                              <div className="row-acts">
                                {st === "pending" ? (
                                  <button type="button" className="iact success" title="Yönetici Onayı"
                                    aria-label={`${r.employeeName}: yönetici onayı ver`}
                                    disabled={approveMut.isPending} onClick={() => approveMut.mutate({ id: r.id, stage: "manager" })}>
                                    <Icon name="check" size={15} />
                                  </button>
                                ) : null}
                                {st === "manager_approved" ? (
                                  <button type="button" className="iact success" title="İK Onayı"
                                    aria-label={`${r.employeeName}: İK onayı ver`}
                                    disabled={approveMut.isPending} onClick={() => approveMut.mutate({ id: r.id, stage: "hr" })}>
                                    <Icon name="checkCircle" size={15} />
                                  </button>
                                ) : null}
                                {(st === "pending" || st === "manager_approved") ? (
                                  <button type="button" className="iact danger" title="Reddet"
                                    aria-label={`${r.employeeName}: talebi reddet`}
                                    disabled={approveMut.isPending}
                                    onClick={() => { setRejectReason(""); setRejectErr(null); setRejectTarget({ id: r.id, code: r.code, who: r.employeeName }); }}>
                                    <Icon name="x" size={15} />
                                  </button>
                                ) : null}
                                {(st === "pending" || st === "manager_approved" || st === "approved") ? (
                                  <button type="button" className="iact" title="İptal et"
                                    aria-label={`${r.employeeName}: talebi iptal et`}
                                    disabled={approveMut.isPending}
                                    onClick={() => setCancelTarget({ id: r.id, code: r.code, who: r.employeeName })}>
                                    <Icon name="minus" size={15} />
                                  </button>
                                ) : null}
                                {terminal ? <span className="hr-muted">—</span> : null}
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dt2-foot"><span>{reqListQ.isLoading ? "Yükleniyor…" : reqListQ.isError ? "—" : `${rows.length} talep`}</span></div>
          </>
        ) : null}

        {/* ── ALT SEKME: İzin Türleri (canManage) ── */}
        {sub === "types" && canManage ? (
          <>
            <div className="dt2-toolbar hr-toolbar">
              <span className="hr-tb-note">İzin türleri, talep ve bakiye hesaplarında kullanılır.</span>
              <span className="grow" />
              <button type="button" className="btn-md primary" onClick={openNewType}>
                <Icon name="plus" size={14} />Tür Ekle
              </button>
            </div>
            <div className="dt2-scroll">
              {typesQ.isError ? (
                <div className="form-banner-err hr-listerr">İzin türleri yüklenemedi: {typesQ.error.message}</div>
              ) : typesQ.isLoading ? (
                <div className="mt-empty">Yükleniyor…</div>
              ) : (typesQ.data ?? []).length === 0 ? (
                <div className="empty-state">
                  <span className="ic" aria-hidden="true"><Icon name="calendar" size={28} /></span>
                  <p className="ttl">İzin türü yok</p>
                  <p className="sub">"Tür Ekle" ile yıllık izin, mazeret, rapor gibi türleri tanımlayın.</p>
                </div>
              ) : (
                <table className="dt2">
                  <thead>
                    <tr>
                      <th>Ad</th>
                      <th style={{ width: 120 }}>Kod</th>
                      <th style={{ width: 120 }}>Ücret</th>
                      <th className="num" style={{ width: 110 }}>Yıllık Hak</th>
                      <th style={{ width: 130 }}>Onay</th>
                      <th style={{ width: 110 }}>Durum</th>
                      <th className="num" style={{ width: 80 }}>Talep</th>
                      <th className="hr-act-col">Eylemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(typesQ.data ?? []).map((t) => (
                      <tr key={t.id}>
                        <td><span className={`badge-pill ${typeBadgeCls(t.color)}`}>{t.name}</span></td>
                        <td className="cell-id hr-cell-sm">{t.code ?? "—"}</td>
                        <td><span className={`badge-pill ${t.paid ? "b-success" : ""}`}><Icon name={t.paid ? "check" : "minus"} size={11} />{t.paid ? "Ücretli" : "Ücretsiz"}</span></td>
                        <td className="cell-num">{t.defaultDays}<span className="unit">gün</span></td>
                        <td><span className={`badge-pill ${t.requiresApproval ? "b-info" : ""}`}><Icon name={t.requiresApproval ? "check" : "minus"} size={11} />{t.requiresApproval ? "Gerekli" : "Serbest"}</span></td>
                        <td><span className={`badge-pill ${t.isActive ? "b-success" : ""}`}><Icon name={t.isActive ? "check" : "x"} size={11} />{t.isActive ? "Aktif" : "Pasif"}</span></td>
                        <td className="cell-num">{t.requestCount}</td>
                        <td>
                          <div className="row-acts">
                            <button type="button" className="iact" title="Düzenle" aria-label={`${t.name}: düzenle`} onClick={() => openEditType(t as TypeRow)}>
                              <Icon name="edit" size={14} />
                            </button>
                            <button type="button" className="iact danger" title="Sil" aria-label={`${t.name}: sil`} onClick={() => setDelType(t as TypeRow)}>
                              <Icon name="trash" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="dt2-foot"><span>{typesQ.isLoading ? "Yükleniyor…" : typesQ.isError ? "—" : `${(typesQ.data ?? []).length} tür`}</span></div>
          </>
        ) : null}
      </div>

      {drawers}
    </>
  );
}